// =============================================================================
// client/src/pages/campaigns/CampaignDetail.jsx | v3.1 | S103 | P9a — W-10
// =============================================================================
// v3.1 (S103, 2026-09-06): the Report tab's JSX moved VERBATIM into
// components/flux/CampaignReport.jsx so the kid's /my-outreach page can render
// the same report (audience="player"). stateFill() and pct() moved with it,
// unchanged. This file passes the state it already owns; every fetch,
// useState, useMemo, useCallback, handler and server-formatted string below
// is byte-identical to v3.0.
// v3.0 (SQ-Chrome S1, 2026-08-27): CHROME ONLY. Board vocabulary from
// lib/flux/board.js. NO DATA LOGIC TOUCHED: every fetch, useState, useMemo,
// useCallback, handler and server-formatted string is byte-identical to v2.0,
// including act()/expectStatus, toggleFilter, filterQuery, pct() and
// ellipsis(). The choropleth's stateFill() hex ramp is UNCHANGED — those
// colours are the data legend, not chrome, and restyling them would relabel
// the map. TabBar is shared with RecruitEdit, a Front Office page outside this
// pass, so it was NOT restyled globally — it gained a `variant` prop instead and
// this page opts in with variant="board". RecruitEdit passes nothing and is
// unchanged. See the 🔴 block at the top of TabBar.jsx for why the seam matters.
// =============================================================================
// One campaign: Report, Recipients, Tracking.
//
// 🔴 THE BROWSER COMPUTES NOTHING HERE. Every tier count, school total, coach
// score, state fill and percentage comes from W-3's /:id/rollup or from
// `stats` (shapeStats). RM had four inline scorers in its client
// (CampaignDashboard.jsx :487, :530, :545-546, :1069) and none is ported (G2,
// invariant 7 — scoring lives in flux_get_campaign_engagement and nowhere else).
//
// 🔴 EVERY FILTER CHANGE RE-FETCHES W-3 WITH THE QUERY. The client never
// re-slices an array it already has. That is not a performance choice: the
// server's filter_values come from the UNFILTERED rows so the chips never
// vanish mid-drilldown, and a client that filtered locally would compute a
// different set from the one the server reports.
//
// `bounced: null` on a coach is an UNKNOWN, not a false — an engagement row
// with no message row. Rendered as "—", never as a clean bill (B-18).
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import usePendingPoll, { isSnapshotPending } from '../../lib/usePendingPoll.js';
import { can } from '../../lib/can.js';
import { useMe } from '../../lib/flux/useMe.js';
import { severityClass } from '../../lib/flux/status.js';
import ConnectGmail from '../../components/flux/ConnectGmail.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import TabBar from '../../components/TabBar.jsx';
import CampaignReport from '../../components/flux/CampaignReport.jsx';
import Toast from '../../components/Toast.jsx';
import {
  PAGE, SHELL, PANEL, PANEL_QUIET, DASH, BTN_LIGHT, BTN_GHOST,
  pill, H1, H2, LABEL, BODY_MUTED,
} from '../../lib/flux/board.js';

const REPORT_PAGE = 50;
const TABS = [
  { id: 'report', label: 'Report' },
  { id: 'recipients', label: 'Recipients' },
  { id: 'tracking', label: 'Tracking' },
];

// A control that sits in the masthead action row: smaller than a full pill,
// same ink and same active-press as the board's other buttons.
const ACT = 'inline-flex items-center justify-center leading-none text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-2 rounded-full border-2 border-[#0E2A20] text-[#0E2A20] bg-white hover:bg-[#F4F4F2] transition-transform active:translate-y-[1px]';

// RM :897 — the nav title ellipsises at 25 characters.
const ellipsis = (s, n = 25) => (String(s || '').length > n ? `${String(s).slice(0, n)}…` : String(s || ''));

// stateFill() and pct() live in components/flux/CampaignReport.jsx (v3.1).


export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useMe();

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [rollupFailed, setRollupFailed] = useState(false);
  const [report, setReport] = useState({ rows: [] });
  const [reportOffset, setReportOffset] = useState(0);

  const [tab, setTab] = useState('report');
  const [filters, setFilters] = useState({ division: [], state: [], region: [] });
  const [divisionTab, setDivisionTab] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);      // { title, body, onConfirm }
  const [showReauth, setShowReauth] = useState(false);

  // tracking tab
  const [linkStats, setLinkStats] = useState([]);
  const [events, setEvents] = useState([]);
  const [includeBots, setIncludeBots] = useState(false);

  const filterQuery = useMemo(() => {
    const q = new URLSearchParams();
    for (const key of ['division', 'state', 'region']) {
      if (filters[key]?.length) q.set(key, filters[key].join(','));
    }
    const s = q.toString();
    return s ? `?${s}` : '';
  }, [filters]);

  const loadCampaign = useCallback(() => {
    // GET /:id returns a FLAT object — the campaign's own columns spread at
    // the top level alongside recruitName, stats and W-1's three keys. It is
    // NOT { campaign, stats }; /:id/report is the one that nests.
    return api.get(`/api/flux/campaigns/${id}`)
      .then((body) => { setCampaign(body || null); setStats(body?.stats || null); setError(null); })
      .catch((err) => setError(err.body?.error || err.message));
  }, [id]);

  const loadRollup = useCallback(() => {
    return api.get(`/api/flux/campaigns/${id}/rollup${filterQuery}`)
      .then((body) => { setRollup(body); setRollupFailed(false); })
      .catch((err) => {
        setRollupFailed(true);
        setToast({ tone: 'error', message: `Report failed: ${err.body?.error || err.message}` });
      });
  }, [id, filterQuery]);

  const loadReport = useCallback(() => {
    // 🔴 /:id/report answers { campaign, stats, emails, limit, offset } and
    // carries NO total. The spec's `pagerRange` assumes one. Rather than invent
    // a page count, the pager below offers Next only while the last page came
    // back FULL — a short page is the end. Reported to OC as a spec/code gap.
    return api.get(`/api/flux/campaigns/${id}/report?limit=${REPORT_PAGE}&offset=${reportOffset}`)
      .then((body) => setReport({ rows: body?.emails || [] }))
      .catch(() => { /* the report tab is the page; this table is one tab */ });
  }, [id, reportOffset]);

  // The rollup and the email table are NOT loaded here: their own effects
  // below already run on mount, and loading them in both places fetched each
  // twice (the rollup is megabytes on a large campaign).
  useEffect(() => {
    setLoading(true);
    Promise.allSettled([loadCampaign()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { loadRollup(); }, [loadRollup]);
  useEffect(() => { loadReport(); }, [loadReport]);

  usePendingPoll(rollup, loadRollup);

  useEffect(() => {
    if (tab !== 'tracking') return;
    // Sweep near-miss, same class as A/B: this route answers a BARE ARRAY of
    // { url, clicks } (flux-track-staff.js:112) — there is no { links } wrapper.
    // The old read was `b?.links || b || []`, which WORKED only through the
    // fallback; the b?.links half was dead and read as if a wrapper existed.
    api.get(`/api/flux/track/link-stats/${id}`)
      .then((rows) => setLinkStats(Array.isArray(rows) ? rows : []))
      .catch(() => setLinkStats([]));
    api.get(`/api/flux/track/events/${id}?limit=50&includeBots=${includeBots}`)
      .then((b) => setEvents(b?.events || [])).catch(() => setEvents([]));
  }, [tab, id, includeBots]);

  // ── actions ───────────────────────────────────────────────────────────────
  // 🔴 J-4. `expectStatus` EXISTS BECAUSE A 202 IS NOT AN OUTCOME.
  // /:id/launch and /:id/resume both go through respondLaunch, which races the
  // operation against LAUNCH_RACE_MS (1500) and answers
  // 202 {accepted:true, note:'Run started…'} when the timer wins — whether or
  // not the operation then succeeds. So `await fn()` resolving means "the
  // server stopped waiting", NOT "it worked", and the old unconditional
  // `${label} — done.` rendered a refusal as a success.
  //
  // ⚠ ONLY the raced routes pass expectStatus. /pause is synchronous and has no
  // race (flux-campaigns.js:1603), and mark-replied / mark-bounced have no
  // campaign status to assert, so they keep the plain path deliberately —
  // asserting a status where none is promised would invent a second bug.
  //
  // The re-read is the same loadCampaign() this already did; the change is that
  // its RESULT now decides the toast instead of arriving after it.
  async function act(label, fn, expectStatus) {
    try {
      await fn();
      if (expectStatus) {
        let row = null;
        try { row = await api.get(`/api/flux/campaigns/${id}`); } catch { row = undefined; }
        if (row === undefined) {
          setToast({ tone: 'error', message: `${label} was accepted but its status could not be confirmed — reload before assuming it took effect.` });
        } else if (expectStatus.includes(row?.status)) {
          setToast({ tone: 'success', message: `${label} — now "${row?.status}".` });
        } else {
          setToast({ tone: 'error', message: `${label} did not take effect — the campaign is still "${row?.status ?? 'unknown'}".` });
        }
      } else {
        setToast({ tone: 'success', message: `${label} — done.` });
      }
      loadCampaign(); loadRollup();
    } catch (err) {
      // The server's refusal is rendered VERBATIM — a `sending` campaign's 409
      // on archive says exactly why, and paraphrasing it costs the operator.
      setToast({ tone: 'error', message: err.body?.error || err.message });
    } finally {
      setConfirm(null);
    }
  }

  function toggleFilter(key, value) {
    setFilters((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  }

  const sd = campaign?.status_display || {};
  const na = campaign?.next_action || {};
  const canPause = can(me, 'campaign.pause');
  const canResume = me?.team?.role === 'admin';
  const canArchive = can(me, 'campaign.archive');

  if (loading && !campaign) return <div className={`${PAGE} p-8 ${BODY_MUTED}`}>Loading…</div>;
  if (error) return <div className={`${PAGE} p-8 text-sm text-red-700`}>{error}</div>;

  // G4: no zero default. Until a rollup arrives (or while a snapshot is pending) CampaignReport shows a
  // plain panel and never renders tiers, so a loading or missing report can no longer read as "0 coaches".
  const tiers = rollup?.tiers;
  const rollupStatus = rollup
    ? (isSnapshotPending(rollup) ? 'pending' : 'ready')
    : (rollupFailed ? 'error' : 'loading');
  const divisions = rollup?.by_division || [];
  const shownDivisions = divisionTab ? divisions.filter((d) => d.division === divisionTab) : divisions;

  return (
    <div className={PAGE}>
      {/* Masthead sits ON the paper ground with a dashed rule under it — the
          board has no solid header bar. */}
      <header className={`${DASH} border-t-0 border-b-2`}>
        <div className={`${SHELL} py-8`}>
          <button type="button" onClick={() => navigate('/campaigns')} className={BTN_GHOST}>
            ← FluxEngine
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap mt-4">
            <div className="min-w-0">
              <h1 className={H1}>
                {campaign?.recruitName || 'Unassigned'} — {ellipsis(campaign?.name)}
              </h1>
              {campaign?.operatorName ? (
                <p className={`${BODY_MUTED} mt-2`}>Operator: {campaign.operatorName}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              {canPause && (campaign?.status === 'sending' || campaign?.status === 'queued') ? (
                <button type="button" className={ACT}
                  onClick={() => act('Paused', () => api.post(`/api/flux/campaigns/${id}/pause`, {}))}>
                  Pause
                </button>
              ) : null}
              {canResume && campaign?.status === 'paused' ? (
                <button type="button" className={ACT}
                  onClick={() => act('Resumed', () => api.post(`/api/flux/campaigns/${id}/resume`, {}), ['sending', 'queued'])}>
                  Resume
                </button>
              ) : null}
              <button type="button" className={ACT}
                onClick={() => navigate(`/campaigns/${id}/edit`)}>
                Edit
              </button>
              {canArchive ? (
                <button
                  type="button"
                  className={`${ACT} border-dashed text-[#5A5A5A]`}
                  onClick={() => setConfirm({
                    title: 'Archive Campaign',
                    body: 'This campaign will be removed from the active dashboard. You can unarchive it later.',
                    onConfirm: () => act('Archived', () => api.patch(`/api/flux/campaigns/${id}/archive`, {})),
                  })}
                >
                  Archive
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-5">
            <span className={`text-[0.55rem] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${severityClass(sd.severity)}`}>
              {sd.badge || '—'}
            </span>
            <span className="text-sm text-[#0E2A20]">{sd.label}</span>
            {na.text ? <span className={BODY_MUTED}>· {na.text}</span> : null}
            {/* Keyed on next_action.action, never on the TOKEN class (§0.2 Q1). */}
            {na.action === 'reauth' ? (
              <button type="button" onClick={() => setShowReauth((v) => !v)}
                className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 border-amber-500 text-amber-800 bg-amber-50">
                {showReauth ? 'Hide' : 'Send Re-Auth Email'}
              </button>
            ) : null}
          </div>
          {showReauth ? (
            <div className="mt-4">
              <p className={`${LABEL} mb-3`}>Emailing the link is P9b. Connect or copy the link here in the meantime.</p>
              <ConnectGmail recruitId={campaign?.recruit_id} gmail={campaign?.send_from_email} />
            </div>
          ) : null}
        </div>
      </header>

      <main className={`${SHELL} py-8`}>
        <TabBar tabs={TABS} activeTab={tab} onSelect={setTab} variant="board" />

        {/* ══ REPORT ══════════════════════════════════════════════════════ */}
        {tab === 'report' ? (
          <CampaignReport
            audience="staff"
            stats={stats}
            rollup={rollup}
            rollupStatus={rollupStatus}
            tiers={tiers}
            shownDivisions={shownDivisions}
            filters={filters}
            toggleFilter={toggleFilter}
            onReset={() => { setFilters({ division: [], state: [], region: [] }); setDivisionTab(null); }}
            divisionTab={divisionTab}
            setDivisionTab={setDivisionTab}
          />
        ) : null}
        {/* ══ RECIPIENTS ══════════════════════════════════════════════════ */}
        {tab === 'recipients' ? (
          <div className="py-6">
            <div className={`${PANEL} overflow-x-auto`}>
              <table className="w-full text-sm">
                <thead className={`text-left ${LABEL} border-b-2 border-dashed border-[#0E2A20]`}>
                  <tr>
                    <th className="px-4 py-3">Recipient</th>
                    <th className="px-4 py-3">School</th>
                    <th className="px-4 py-3">Div</th>
                    <th className="px-4 py-3">State</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Sent</th>
                    <th className="px-4 py-3">Error</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0E2A20]/15">
                  {report.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-3 text-[#0E2A20]">{r.recipient_name || r.recipient_email}</td>
                      <td className="px-4 py-3 text-[#5A5A5A]">{r.recipient_school}</td>
                      <td className="px-4 py-3 text-[#5A5A5A]">{r.division || '—'}</td>
                      <td className="px-4 py-3 text-[#5A5A5A]">{r.state || '—'}</td>
                      <td className="px-4 py-3 text-[#5A5A5A]">
                        {r.status}
                        {r.opened ? ' · viewed' : ''}{r.clicked ? ' · clicked' : ''}
                        {r.replied ? ' · replied' : ''}{r.bounced ? ' · bounced' : ''}
                      </td>
                      <td className="px-4 py-3 text-[#5A5A5A]">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                      <td className="px-4 py-3 text-red-700">{r.error_message || ''}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <button type="button" className={pill(false)}
                            onClick={() => {
                              const note = window.prompt('Reply note (optional, max 500 chars)') ?? '';
                              act('Marked replied', () => api.post(`/api/flux/track/reply/${r.id}`, { note }).then(loadReport));
                            }}>
                            Replied
                          </button>
                          <button type="button" className={`${pill(false)} border-amber-500 text-amber-800`}
                            onClick={() => {
                              const reason = window.prompt('Bounce reason (optional, max 500 chars)') ?? '';
                              act('Marked bounced', () => api.post(`/api/flux/track/bounce/${r.id}`, { reason }).then(loadReport));
                            }}>
                            Bounced
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(reportOffset > 0 || report.rows.length === REPORT_PAGE) ? (
              <div className="flex items-center gap-3 mt-5">
                <button type="button" disabled={reportOffset === 0}
                  onClick={() => setReportOffset(Math.max(0, reportOffset - REPORT_PAGE))}
                  className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`}>Previous</button>
                <span className={BODY_MUTED}>
                  {reportOffset + 1}–{reportOffset + report.rows.length}
                </span>
                <button type="button" disabled={report.rows.length < REPORT_PAGE}
                  onClick={() => setReportOffset(reportOffset + REPORT_PAGE)}
                  className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`}>Next</button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ══ TRACKING ════════════════════════════════════════════════════ */}
        {tab === 'tracking' ? (
          <div className="py-6 space-y-6">
            <section className={`${PANEL} p-6`}>
              <h3 className={`${H2} mb-4`}>Links</h3>
              {linkStats.length === 0 ? <p className={BODY_MUTED}>No link clicks yet.</p> : (
                <ul className="space-y-1.5">
                  {linkStats.map((l) => (
                    <li key={l.url} className="text-sm text-[#0E2A20] flex justify-between gap-3">
                      <span className="truncate">{l.url}</span>
                      <span className="text-[#5A5A5A]">{l.clicks}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className={`${PANEL} p-6`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className={H2}>Events</h3>
                {/* The canary instrument — bot-suspect events are hidden by
                    default and shown on demand, never silently folded in. */}
                <label className={`${BODY_MUTED} flex items-center gap-2`}>
                  <input type="checkbox" checked={includeBots} onChange={(e) => setIncludeBots(e.target.checked)} className="accent-[#0E2A20]" />
                  show bot-suspect events
                </label>
              </div>
              {events.length === 0 ? <p className={BODY_MUTED}>No events yet.</p> : (
                <ul className="space-y-1.5">
                  {events.map((e) => (
                    <li key={e.id} className={BODY_MUTED}>
                      {e.created_at ? new Date(e.created_at).toLocaleString() : '—'} · {e.event_type} · {e.email_messages?.recipient_email || ''}
                      {e.is_bot_suspect ? ' · bot-suspect' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </main>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel="Confirm"
        onConfirm={() => confirm?.onConfirm?.()}
        onCancel={() => setConfirm(null)}
      />
      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}
